import { Box, Text, Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import {
  getSelectedDashboardScenario,
  getTimeFilter,
  getDashboardReportingPeriod,
  getCompareDashboardScenario,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { getReportingPeriodDisplay } from 'utils/reportingPeriodUtils';

import { PageInstruments } from './Instruments/proptypes';
import PageGrid from './PageGrid';

export interface PreviewProperties {
  selectedPages: any[];
  allPageInstruments: PageInstruments;
  allInstrumentsData: PageInstruments;
  isPageDataLoading: boolean;
  isPageInstrumentsLoading: boolean;
}

const Preview = ({
  selectedPages,
  allPageInstruments,
  allInstrumentsData,
  isPageDataLoading,
  isPageInstrumentsLoading,
}: PreviewProperties) => {
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const timeFilter = useSelector(getTimeFilter);
  const dashboardReportingPeriod = useSelector(getDashboardReportingPeriod);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const model = useSelector(getBlocks);
  const fyStartMonth = useMemo(() => {
    const month = model?.time_properties?.fy_start_month;
    return (month as string | undefined) ?? undefined;
  }, [model?.time_properties?.fy_start_month]);

  if (isPageDataLoading || isPageInstrumentsLoading) {
    return <Box>Loading preview...</Box>;
  }

  return (
    <Box width='100%' display='flex' flexDirection='column' alignItems='center'>
      {selectedPages
        .sort((a: any, b: any) => a.position - b.position)
        .map((page: any, index: number) => {
          const reportingPeriodDisplay = dashboardReportingPeriod
            ? getReportingPeriodDisplay(
                dashboardReportingPeriod,
                fyStartMonth,
                dashboardReportingPeriod.preset || 'Custom',
              )
            : null;
          const hasDescription = Boolean(page?.description);

          return (
            <Box
              key={page.id}
              className={`page-container page-${page.id} ${index === 0 ? '' : 'page-break'}`}
              data-page-number={`Page ${index + 1} of ${selectedPages.length}`}
            >
              <Text fontWeight='bold' mb={hasDescription ? 0 : 2} color='black' fontSize='xl'>
                {page.name}
              </Text>
              {hasDescription && (
                <Text color='black' fontSize='md' mb={1}>
                  {page.description}
                </Text>
              )}
              {hasDescription && (
                <Text color='black' fontSize='md' mb={1}>
                  {page.description}
                </Text>
              )}
              <Flex flexWrap='wrap' gap={1} mb={1}>
                <Box>
                  <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                    {` Scenario : ${selectedScenario?.name || 'No scenario selected'} `}
                  </Text>
                </Box>
                {compareScenario ? (
                  <Box>
                    <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                      {`vs ${compareScenario?.name || ''} `}
                    </Text>
                  </Box>
                ) : null}
                <Box>
                  <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                    {`| Display Timeframe : ${timeFilter?.start || 'N/A'} - ${
                      timeFilter?.end || 'N/A'
                    }`}
                  </Text>
                </Box>
                {reportingPeriodDisplay ? (
                  <Box>
                    <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                      {`| Reporting Period : ${reportingPeriodDisplay}`}
                    </Text>
                  </Box>
                ) : null}
              </Flex>
              <PageGrid
                page={page}
                allInstruments={allPageInstruments[page.id] || []}
                allInstrumentsData={allInstrumentsData[page.id] || []}
              />
            </Box>
          );
        })}
    </Box>
  );
};

export default Preview;
